// import type { LangKey } from '@airwallex/core/dist/global';
//import type { DetailsProps } from '@airwallex-dev-scale/core/dist/address-autocomplete';

import type { AccountState } from '@/contexts/containerProps/accountState';
import { AccountKybStatus } from '@/graphql/generated';
import type { BusinessModelType } from '@/pages/initialPaymentActivation/types';

import { STATE } from './constants/states';



export const PaymentLandingPageUrl = `/app1/acquiring`;
export const PaymentSettingUrl = `/app1/acquiring/settings`;


export const getAirwallexEnv = (): 'staging' | 'demo' | 'prod' | undefined => {
  const hostName = window.location.hostname;
  if (hostName.indexOf('staging') !== -1) {
    return 'staging';
  } else if (hostName.indexOf('demo') !== -1) {
    return 'demo';
  } else if (hostName === 'www.airwallex.com') {
    return 'prod';
  }
  return 'staging';
};

export const getScaleWebappThemeConfig = ({
  account,
}: {
  account?: AccountState;
}) =>
  // first is for connected accounts (child), second for partner/platform (parent)
  account?.scaleConnectedPlatformInfo?.webappTheme ||
  account?.clientApiSettings?.connectedAccountSettings?.webappTheme;

export const isPba = ({ account }: { account?: AccountState }) =>
  !!getScaleWebappThemeConfig({ account })?.contentfulSlug;

export const getBrandText = ({
  account,
  langKey,
}: {
  account?: AccountState;
  langKey: any;
}) => {
  return getScaleWebappThemeConfig({ account })?.brand?.text?.[langKey];
};

export function generatedStringHash(str: string) {
  let hash = 0,
    i,
    chr;
  if (str.length === 0) {
    return hash.toString(16);
  }
  for (i = 0; i < str.length; i++) {
    chr = str.charCodeAt(i);
    hash = (hash << 5) - hash + chr;
    hash |= 0; // Convert to 32bit integer
  }
  return hash.toString(16);
}

export const KYB_STATUS_SDK_EVENT_MAPPING = {
  [AccountKybStatus.Approved]: 'APPROVED',
  [AccountKybStatus.Accepted]: 'ACCEPTED',
  [AccountKybStatus.Processing]: 'PENDING_REVIEW',
  [AccountKybStatus.NoStatus]: 'INIT',
  [AccountKybStatus.InReview]: 'IN_REVIEW',
  [AccountKybStatus.InReviewWithSupplement]: 'IN_REVIEW',
  [AccountKybStatus.Rejected]: 'REJECTED',
  [AccountKybStatus.RejectedManual]: 'REJECTED',
};

export const BusinessModelTypeContentKey = {
  online: 'KYB_STORE_ONLINE_TITLE',
  others: 'KYB_STORE_OTHERS_TITLE',
  physical: 'KYB_STORE_PHYSICAL_TITLE',
};

interface B2PMappingType {
  online: string[];
  others: string[];
  physical: string[];
}

export const BusinessModal2ProductTypesMapping: B2PMappingType = {
  online: ['ONLINE_PAYMENTS', 'PAYMENT_LINK', 'XERO_INVOICE'],
  others: ['PAYMENT_LINK', 'XERO_INVOICE'],
  physical: ['PAYMENT_LINK'],
};

export const HKBusinessModal2ProductTypesMapping: B2PMappingType = {
  online: ['ONLINE_PAYMENTS', 'XERO_INVOICE'],
  others: ['PAYMENT_LINK', 'XERO_INVOICE'],
  physical: ['PAYMENT_LINK'],
};

export const ProductMapping = {
  ONLINE_PAYMENTS: 'ONLINE_PAYMENTS',
  XERO_INVOICE: 'INVOICES',
  PAYMENT_LINK: 'PAYMENT_LINKS',
};

const businessModelsToProductConverter = (
  mapping: B2PMappingType,
  businessModel?: BusinessModelType[],
): string[] => {
  return [
    // @ts-ignore
    ...(businessModel?.reduce((acc, cur) => {
      // @ts-ignore
      mapping[cur]?.forEach((i) => acc.add(i));
      return acc;
    }, new Set<string>()) || []),
  ];
};

export const getPaymentProductsEnumList = ({
  businessModel,
  countryCode,
}: {
  businessModel?: BusinessModelType[];
  countryCode?: string;
}) => {
  const showXeroInvoice = XERO_INVOICE_WHITE_COUNTRY_LIST.includes(
    countryCode as string,
  );
  // the mapping relationship is here - https://docs.google.com/spreadsheets/d/1kT4_Vy37N-MXPdTEuGbJdF44BFAW7cSKSqS4CWr1CZE/edit#gid=679929986
  let paymentProductsEnumList =
    countryCode === 'HK'
      ? businessModelsToProductConverter(
          HKBusinessModal2ProductTypesMapping,
          businessModel,
        )
      : businessModelsToProductConverter(
          BusinessModal2ProductTypesMapping,
          businessModel,
        );
  // for NON AU, UK, HK, SG customers, need to hide the xero invoice for now, but in future we should invoke an API to fetch the supported payment methods
  if (!showXeroInvoice) {
    paymentProductsEnumList = paymentProductsEnumList.filter(
      (i) => i !== 'XERO_INVOICE',
    );
  }

  return paymentProductsEnumList;
};

export const XERO_INVOICE_WHITE_COUNTRY_LIST = ['AU', 'UK', 'HK', 'SG'];

export const appPath = `/${window.location.pathname.split('/')[1]}`;

export const paymentSettingUrl = `${appPath}/acquiring/settings`;

export const ongoingKybRedirectUrl = `${appPath}/acquiring/payment-methods/my-pms`;

export const ongoingKybRedirectUrlOnReserveSelection = `${appPath}/acquiring/payment-methods/my-pms?hasSelectedReserve=true`;

export const ongoingKybRedirectUrlOnSubmission = `${appPath}/acquiring/payment-methods/my-pms?hasFinishedPaymentActivation=true`;

export const getAwxReferrerLinkOrDashboard = () => {
  const url = new URLSearchParams(window.location.search).get('from');
  return url || `${appPath}/dashboard`;
};

export const redirectToPaymentSettings = () => {
  window.location.assign(paymentSettingUrl);
};

export const redirectWhenExitInitialKyb = () => {
  window.location.assign(paymentSettingUrl);
};

export const redirectWhenAbortOngoingKyb = () => {
  window.location.assign(ongoingKybRedirectUrl);
};

export const redirectAfterSubmitOngoingKybCase = () => {
  window.location.assign(ongoingKybRedirectUrlOnSubmission);
};

export const redirectAfterSelectReserve = () => {
  window.location.assign(ongoingKybRedirectUrlOnReserveSelection);
};

export const PaymentMethodWithMultiNames = [
  'GRABPAY',
  'ONLINE_BANKING',
  'BANK_TRANSFER',
  'SEVEN_ELEVEN',
];

export const convertPopulateAddress = ({
  details,
  countryCode,
}: {
  details: any;
  countryCode: string;
}) => {
  const {
    administrativeAreaLevel1,
    administrativeAreaLevel1LongName,
    administrativeAreaLevel2,
    streetAddress,
    postCode,
  } = details;

  const isStateRenderAsDropDown = Object.keys(STATE).includes(countryCode);
  const rawState = administrativeAreaLevel1.split('-')[1];
  const state = isStateRenderAsDropDown
    ? rawState
    : administrativeAreaLevel1LongName;

  return {
    line: streetAddress,
    city: administrativeAreaLevel2,
    zipcode: postCode,
    state,
  };
};
